import React from 'react';
import { Loader2, AlertCircle, Star, TruckIcon } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

/**
 * SuppliersBlock — Lieferanten & Konditionen (Phase 3a).
 *
 * Props:
 *   suppliers   Array|null   Ergebnis von getLagerSuppliers
 *   loading     boolean
 *   error       string|null
 *   meins       string       Basismengeneinheit des Materials (fuer Anzeige)
 */
const SuppliersBlock = ({ suppliers, loading, error, meins }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const cellMuted  = isDarkMode ? 'text-white/45' : 'text-slate-400';
  const cellVal    = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const sectionTitle = `text-xs font-semibold uppercase tracking-wide mb-3 ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`;

  const formatPrice = (price, currency) => {
    if (price == null || price === '') return '—';
    const num = parseFloat(price);
    if (Number.isNaN(num)) return '—';
    const fmt = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
    return currency ? `${fmt} ${currency}` : fmt;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <Loader2 className={`w-4 h-4 animate-spin ${isDarkMode ? 'text-brand' : 'text-brand'}`} />
        <span className={`text-xs ${cellMuted}`}>{t('lm.suppliersLoading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 py-3">
        <AlertCircle className={`w-4 h-4 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
        <span className={`text-xs ${cellMuted}`}>{t('lm.suppliersError')}: {error}</span>
      </div>
    );
  }

  if (!Array.isArray(suppliers) || suppliers.length === 0) {
    return (
      <p className={`text-xs ${cellMuted} py-2`}>{t('lm.suppliersEmpty')}</p>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <TruckIcon className={`w-4 h-4 ${isDarkMode ? 'text-brand/70' : 'text-brand'}`} />
        <p className={sectionTitle}>{t('lm.suppliersTitle')}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className={`border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'}`}>
              <th className={`text-left pb-1.5 pr-3 font-semibold ${cellMuted}`}>{t('lm.suppliersColSupplier')}</th>
              <th className={`text-right pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.suppliersColNetpr')}</th>
              <th className={`text-right pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.suppliersColPeinh')}</th>
              <th className={`text-right pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.suppliersColDelivery')}</th>
              <th className={`text-left pb-1.5 pr-2 font-semibold ${cellMuted}`}>{t('lm.suppliersColContract')}</th>
              <th className={`text-left pb-1.5 font-semibold ${cellMuted}`}>{t('lm.suppliersColValidTo')}</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.map((sup, idx) => {
              const hasPeinh = sup.peinh != null && sup.peinh > 1;
              return (
                <tr
                  key={sup.lifnr ?? idx}
                  className={`border-b ${isDarkMode ? 'border-white/5' : 'border-slate-50'}`}
                >
                  {/* Lieferant */}
                  <td className={`py-1.5 pr-3 ${cellVal}`}>
                    <div className="flex items-center gap-1.5">
                      {sup.is_cheapest && (
                        <Star
                          className={`w-3 h-3 flex-shrink-0 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-500'}`}
                          fill="currentColor"
                          title={t('lm.suppliersCheapest')}
                        />
                      )}
                      <span className={sup.is_cheapest ? `font-semibold ${cellVal}` : cellVal}>
                        {sup.lieferant_name ?? sup.lifnr ?? '—'}
                      </span>
                    </div>
                  </td>

                  {/* Nettopreis + optionaler Stückpreis */}
                  <td className={`py-1.5 pr-2 text-right ${cellVal}`}>
                    <span>{formatPrice(sup.netpr, sup.waehrung)}</span>
                    {hasPeinh && sup.price_per_unit != null && (
                      <span className={`ml-1 ${cellMuted}`}>
                        ({formatPrice(sup.price_per_unit, null)}&nbsp;{t('lm.suppliersPerUnit')})
                      </span>
                    )}
                  </td>

                  {/* Preiseinheit */}
                  <td className={`py-1.5 pr-2 text-right font-mono ${cellMuted}`}>
                    {sup.peinh ?? 1}&nbsp;{meins || ''}
                  </td>

                  {/* Lieferzeit */}
                  <td className={`py-1.5 pr-2 text-right ${cellMuted}`}>
                    {sup.planned_delivery_time != null
                      ? t('lm.suppliersDeliveryDays', { days: sup.planned_delivery_time })
                      : '—'}
                  </td>

                  {/* Vertragsnummer */}
                  <td className={`py-1.5 pr-2 font-mono ${cellMuted}`}>
                    {sup.contract_no ?? t('lm.suppliersNoContract')}
                  </td>

                  {/* Gueltig bis */}
                  <td className={`py-1.5 ${cellMuted}`}>
                    {formatDate(sup.valid_to)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SuppliersBlock;
